/*
 * Figma `118:277` and `113:281` — the readiness row.
 *
 * Three or four cards above a configuration screen, each answering one question: is this part
 * working right now. It is the first thing on every integration and payment screen in the frozen
 * design, and deliberately so — the SafetyTemplate puts health before any control that changes it,
 * because someone arriving to fix something needs to know what is broken before they are offered a
 * switch.
 *
 * The glyph is not the state. Tone colours the card, but the status line spells the state out in
 * words, so it survives greyscale, a screen reader, and a colour-blind reader.
 */
import type { ReactNode } from 'react';
import { IconSuccess, IconWarning, IconDanger, IconInfo } from './icons';

export type ReadinessTone = 'ready' | 'attention' | 'down' | 'unknown';

const GLYPH: Record<ReadinessTone, (p: { size?: number }) => ReactNode> = {
  ready: IconSuccess, attention: IconWarning, down: IconDanger, unknown: IconInfo,
};

export function ReadinessRow({ children, label }: { children: ReactNode; label: string }) {
  return <div className="jt-readiness-row" role="group" aria-label={label}>{children}</div>;
}

export function ReadinessCard({ title, status, tone = 'unknown', detail, action }: {
  title: string;
  /** The state in words — "พร้อม", "ยังไม่ได้ตั้งค่า". Never left to the colour alone. */
  status: string;
  tone?: ReadinessTone;
  detail?: ReactNode;
  action?: ReactNode;
}) {
  const Glyph = GLYPH[tone];
  return (
    <div className="jt-readiness" data-tone={tone}>
      <span className="jt-readiness-glyph" aria-hidden="true"><Glyph size={17} /></span>
      <div className="jt-readiness-copy">
        <p className="jt-readiness-title">{title}</p>
        <p className="jt-readiness-status">{status}</p>
        {detail && <p className="jt-readiness-detail">{detail}</p>}
      </div>
      {action && <div className="jt-readiness-action">{action}</div>}
    </div>
  );
}
